/**
 * Unified Scanning System
 *
 * Provides consistent scanning behavior across all scanning-based input methods:
 * - EyeControl Device
 * - External Switches
 * - Mouth Gestures
 *
 * The number of inputs (gestures/switches) determines the available actions:
 * - 1 input: Select only (automatic scanning)
 * - 2 inputs: Select + Cancel (automatic scanning)
 * - 3 inputs: Select + Next + Previous (manual scanning)
 * - 4 inputs: Select + Next + Previous + Back (manual scanning)
 * - 5+ inputs: Directional navigation (manual scanning)
 */

export type ScanningAction = 'select' | 'cancel' | 'next' | 'prev' | 'back' | 'up' | 'down' | 'left' | 'right';

/**
 * Get available actions based on input count
 * Unified logic for all scanning methods
 */
export function getAvailableActionsForInputCount(inputCount: number): ScanningAction[] {
  if (inputCount >= 5) {
    return ['select', 'up', 'down', 'left', 'right'];
  } else if (inputCount === 4) {
    return ['select', 'next', 'prev', 'back'];
  } else if (inputCount === 3) {
    return ['select', 'next', 'prev'];
  } else if (inputCount === 2) {
    return ['select', 'cancel'];
  } else {
    return ['select'];
  }
}

/**
 * Determine if scanning MUST be automatic based on input count
 * Per SRS:
 * - 1-2 inputs: MUST be automatic (user has no choice, timer moves focus)
 * - 3-5 inputs: User can CHOOSE between automatic and manual
 */
export function isAutomaticScanning(inputCount: number): boolean {
  return inputCount <= 2;
}

/**
 * Dispatch a scanning action event
 * All scanning input methods (external switches, mouth gestures, eyecontrol)
 * should use this to trigger actions
 */
export function dispatchScanningAction(action: ScanningAction) {
  window.dispatchEvent(new CustomEvent('scanningAction', {
    detail: { action }
  }));
}

/**
 * Action labels — fallback translation keys
 */
export const ACTION_LABELS: Record<ScanningAction, string> = {
  select: 'actionSelect',
  cancel: 'actionCancel',
  next: 'actionNext',
  prev: 'actionPrev',
  back: 'actionBack',
  up: 'actionUp',
  down: 'actionDown',
  left: 'actionLeft',
  right: 'actionRight'
};

/**
 * Get display label for action (returns translation key)
 */
export function getActionLabel(action: ScanningAction): string {
  return ACTION_LABELS[action] || action;
}
